import { Tag } from 'antd'
import { ERoles } from 'models/auth'
import { EProductStatus, IActionsProductRequest } from 'models/product'

interface IProductStatusItemData {
  key: string
  label: React.ReactNode
}

export const ENABLE_PRODUCT_STATUS_KEY = 'ENABLE_PRODUCT'

// Меньше этого порога по любой стороне фото отклоняет и бэкенд, и каталог VK Ads
export const MIN_PRODUCT_IMAGE_SIDE = 600

// Метка ещё не залитых файлов в photoOrder: имена им даёт сервер при сохранении
export const NEW_PHOTO_TOKEN = 'new:'

export const TAX_OPTIONS = [
  { value: 5, label: '5%' },
  { value: 10, label: '10%' },
  { value: 22, label: '22%' },
  { value: 0, label: 'Без налога' }
]

export const productTags = {
  [EProductStatus.MODERATION]: {
    value: EProductStatus.MODERATION,
    label: 'На модерации',
    color: 'geekblue'
  },
  [EProductStatus.PUBLISHED]: {
    value: EProductStatus.PUBLISHED,
    label: 'Опубликован',
    color: 'green'
  },
  [EProductStatus.DRAFT]: {
    value: EProductStatus.DRAFT,
    label: 'Черновик',
    color: 'orange'
  },
  [EProductStatus.REJECTED]: {
    value: EProductStatus.REJECTED,
    label: 'Отклонен',
    color: 'red'
  },
  [EProductStatus.ARCHIVED]: {
    value: EProductStatus.ARCHIVED,
    label: 'Удален',
    color: 'red'
  },
  [EProductStatus.DISABLED]: {
    value: EProductStatus.DISABLED,
    label: 'Отключен',
    color: 'default'
  },
  [EProductStatus.BLOCKED]: {
    value: EProductStatus.BLOCKED,
    label: 'Заблокирован',
    color: 'purple'
  }
}

const publishedStatus: IProductStatusItemData = {
  key: EProductStatus.PUBLISHED,
  label: (
    <Tag color={productTags[EProductStatus.PUBLISHED].color}>
      {productTags[EProductStatus.PUBLISHED].label}
    </Tag>
  )
}

const draftedStatus: IProductStatusItemData = {
  key: EProductStatus.DRAFT,
  label: (
    <Tag color={productTags[EProductStatus.DRAFT].color}>
      {productTags[EProductStatus.DRAFT].label}
    </Tag>
  )
}

// Продавец выводит товар из «Отклонен» только повторной модерацией: тот же
// toPublish, но бэкенд ставит «На модерации»
const resubmitStatus: IProductStatusItemData = {
  key: EProductStatus.PUBLISHED,
  label: (
    <Tag color={productTags[EProductStatus.MODERATION].color}>
      Отправить на модерацию
    </Tag>
  )
}

const rejectedStatus: IProductStatusItemData = {
  key: EProductStatus.REJECTED,
  label: (
    <Tag color={productTags[EProductStatus.REJECTED].color}>
      {productTags[EProductStatus.REJECTED].label}
    </Tag>
  )
}

const disabledStatus: IProductStatusItemData = {
  key: EProductStatus.DISABLED,
  label: (
    <Tag color={productTags[EProductStatus.DISABLED].color}>
      {productTags[EProductStatus.DISABLED].label}
    </Tag>
  )
}

const enableStatus: IProductStatusItemData = {
  key: ENABLE_PRODUCT_STATUS_KEY,
  label: <Tag color='green'>Включить</Tag>
}

// подсказка продавцу: тег «Заблокирован» некликабелен, статус меняет только администрация
export const BLOCKED_PRODUCT_HINT =
  'Товар заблокирован администрацией площадки и снят с продажи. Изменить статус может только администрация — обратитесь к менеджеру HorseSmart.'

const blockedStatus: IProductStatusItemData = {
  key: EProductStatus.BLOCKED,
  label: (
    <Tag color={productTags[EProductStatus.BLOCKED].color}>
      {productTags[EProductStatus.BLOCKED].label}
    </Tag>
  )
}

// «Заблокирован» ставит и снимает только супер-админ; продавец этот пункт не видит
const withBlocked = (
  items: IProductStatusItemData[],
  role: ERoles
): IProductStatusItemData[] =>
  role === ERoles.ADMIN ? [...items, blockedStatus] : items

const rejectedStatusItems = (role: ERoles): IProductStatusItemData[] =>
  role === ERoles.ADMIN
    ? withBlocked([publishedStatus, draftedStatus], role)
    : [resubmitStatus]

export const getStatusAfterAction = (
  key: string,
  status: EProductStatus,
  role: ERoles
): EProductStatus =>
  key === EProductStatus.PUBLISHED &&
  status === EProductStatus.REJECTED &&
  role !== ERoles.ADMIN
    ? EProductStatus.MODERATION
    : (key as EProductStatus)

// «Отклонен» — решение площадки по итогам модерации: отклоняют только товар
// на модерации, и бэкенд отклонение из других статусов не принимает
export const getProductStatusItems = (
  status: EProductStatus,
  role: ERoles
): IProductStatusItemData[] => {
  if (status === EProductStatus.BLOCKED) {
    return role === ERoles.ADMIN ? [publishedStatus, draftedStatus] : []
  }
  if (status === EProductStatus.MODERATION) {
    if (role === ERoles.ADMIN) {
      return [publishedStatus, draftedStatus, rejectedStatus]
    }
    return []
  }
  if (status === EProductStatus.PUBLISHED) {
    return withBlocked([draftedStatus], role)
  }
  if (status === EProductStatus.DRAFT) {
    return withBlocked([publishedStatus], role)
  }
  if (status === EProductStatus.REJECTED) {
    return rejectedStatusItems(role)
  }
  return []
}

const archiveStatus: IProductStatusItemData = {
  key: EProductStatus.ARCHIVED,
  label: <Tag color={productTags[EProductStatus.ARCHIVED].color}>Удалить</Tag>
}

// Вариация публикуется, отключается и включается сама по себе, но бэкенд пускает
// её на витрину только при опубликованном основном товаре. Отключение основного
// товара действует на всю группу
export const getVariationStatusItems = (
  status: EProductStatus,
  role: ERoles,
  isParentPublished: boolean
): IProductStatusItemData[] => {
  const menu =
    status === EProductStatus.PUBLISHED
      ? getProductStatusItemsWithManualToggle
      : getProductStatusItems
  const items = (
    status === EProductStatus.DISABLED
      ? [enableStatus, draftedStatus]
      : menu(status, role)
  ).filter(
    (item) =>
      isParentPublished ||
      (item.key !== EProductStatus.PUBLISHED && item !== enableStatus)
  )
  return items.length ? [...items, archiveStatus] : []
}

// Пункт меню статуса → действие PUT admin/products; «Включить»/«Отключить» идут отдельным запросом
const statusActions: Partial<Record<string, keyof IActionsProductRequest>> = {
  [EProductStatus.PUBLISHED]: 'toPublish',
  [EProductStatus.DRAFT]: 'toDraft',
  [EProductStatus.REJECTED]: 'toReject',
  [EProductStatus.BLOCKED]: 'toBlock',
  [EProductStatus.ARCHIVED]: 'toDelete'
}

export const getProductStatusAction = (
  key: string,
  id: string
): IActionsProductRequest | null => {
  const action = statusActions[key]
  return action ? { [action]: id } : null
}

export const getProductStatusItemsWithManualToggle = (
  status: EProductStatus,
  role: ERoles
): IProductStatusItemData[] => {
  if (status === EProductStatus.BLOCKED) {
    // «Отключен»/«Включить» из блокировки недоступны — только обычные статусы
    return role === ERoles.ADMIN ? [publishedStatus, draftedStatus] : []
  }
  if (status === EProductStatus.DISABLED) {
    return withBlocked([enableStatus], role)
  }
  // Отключают только опубликованный товар: неопубликованный просто не публикуют
  if (status === EProductStatus.MODERATION) {
    if (role === ERoles.ADMIN) {
      return withBlocked([publishedStatus, draftedStatus, rejectedStatus], role)
    }
    return []
  }
  if (status === EProductStatus.PUBLISHED) {
    return withBlocked([draftedStatus, disabledStatus], role)
  }
  if (status === EProductStatus.DRAFT) {
    return withBlocked([publishedStatus], role)
  }
  if (status === EProductStatus.REJECTED) {
    return rejectedStatusItems(role)
  }
  return []
}
